import { z } from "zod";
import { type AuthUser } from "@openings/api-contract";

/**
 * @deprecated Use `AuthUser` from `@openings/api-contract` directly.
 * Kept for backwards compatibility — this is the same type the API returns
 * for login / register / me endpoints.
 */
export type ApiUser = AuthUser;

export const baseOk = <T extends z.ZodType>(data: T) =>
  z.object({ ok: z.literal(true), data });
export const baseErr = z.object({
  ok: z.literal(false),
  error: z.object({ code: z.string(), message: z.string() }),
});

export type ApiError = z.infer<typeof baseErr>["error"];
export type ApiOk<T> = { ok: true; data: T };
export type ApiErr = { ok: false; error: { code: string; message: string } };
export type ApiResult<T> = ApiOk<T> | ApiErr;

export type DbDateString =
  `${number}${number}${number}${number}-${number}${number}-${number}${number}`;
export type DbTimeString = `${number}${number}:${number}${number}`;

export interface ApiClientOptions {
  baseUrl: string;
  fetchImpl?: typeof fetch;
  credentials?: RequestCredentials;
}

/**
 * Narrow mutation controls exposed by the shared client. Arbitrary headers
 * are intentionally not accepted so callers cannot replace authentication
 * or version headers.
 */
export interface MutationRequestOptions {
  idempotencyKey?: string;
}

export function resolveFetch(opts?: ApiClientOptions) {
  if (opts?.fetchImpl) {
    return opts.fetchImpl;
  }
  const nativeFetch = globalThis.fetch;
  if (typeof nativeFetch === "function") {
    return nativeFetch.bind(globalThis) as typeof fetch;
  }
  throw new Error("Fetch API is not available in this environment");
}

export async function handle<T>(res: Response, schema: z.ZodType): Promise<T> {
  const json = await res.json().catch(() => ({}));
  const okParsed = schema.safeParse(json);
  if (okParsed.success) return okParsed.data as T;
  const errParsed = baseErr.safeParse(json);
  if (errParsed.success) {
    throw Object.assign(new Error(errParsed.data.error.message), {
      code: errParsed.data.error.code,
      status: res.status,
    });
  }

  // Fallback: accept responses with "success" or "ok" keys that didn't match
  // the schema. This can hide schema drift — log a warning so it's visible.
  if (res.ok && json && typeof json === "object") {
    const hasLegacyShape =
      "success" in (json as Record<string, unknown>) ||
      "ok" in (json as Record<string, unknown>);
    if (hasLegacyShape) {
      if (typeof console !== "undefined" && console.warn) {
        console.warn(
          `[api-client] Response matched legacy shape but not schema for ${res.url} (${res.status})`,
        );
      }
      return json as T;
    }
  }

  throw Object.assign(new Error(`Unexpected response (${res.status})`), {
    status: res.status,
  });
}

export function buildUrl(base: string, path: string) {
  return base.replace(/\/$/, "") + path;
}

/**
 * STRICT envelope handling for commerce domains. Unlike `handle`, there is NO
 * permissive legacy fallback: a success payload that does not match its schema
 * fails closed, so financial and redaction shapes cannot be bypassed by a
 * malformed-but-ok-looking response. Existing domains keep `handle`.
 */
export async function handleStrict<T>(
  res: Response,
  schema: z.ZodType,
): Promise<T> {
  const json = await res.json().catch(() => ({}));
  const okParsed = schema.safeParse(json);
  if (okParsed.success) return okParsed.data as T;
  const errParsed = baseErr.safeParse(json);
  if (errParsed.success) {
    throw Object.assign(new Error(errParsed.data.error.message), {
      code: errParsed.data.error.code,
      status: res.status,
    });
  }
  // A throttled request carries the rate limiter's own body, not the API
  // error envelope. Name it honestly so surfaces can offer a retry instead of
  // reporting a malformed contract (Sep 2026: staff reports showed
  // "Malformed commerce response (429)" after a busy E2E session).
  if (res.status === 429) {
    throw Object.assign(
      new Error("Too many requests. Please try again in a moment."),
      { status: 429, code: "RATE_LIMITED" },
    );
  }
  throw Object.assign(
    new Error(`Malformed commerce response (${res.status})`),
    { status: res.status, code: "MALFORMED_RESPONSE" },
  );
}

/** Response content types the raw text helper accepts by default. */
export const RAW_TEXT_CONTENT_TYPES = ["text/csv", "text/plain"] as const;

export interface RawTextResponse {
  content: string;
  contentType: string;
  /** Parsed from `content-disposition` when the server names the download. */
  filename: string | null;
}

function mutationHeaders(
  init?: MutationRequestOptions,
): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (init?.idempotencyKey !== undefined) {
    if (!/^[\x21-\x7e]{1,255}$/.test(init.idempotencyKey)) {
      throw new Error(
        "Idempotency key must be 1-255 visible ASCII characters",
      );
    }
    headers["Idempotency-Key"] = init.idempotencyKey;
  }
  return headers;
}

export class BaseHttpClient {
  protected base: string;
  protected fetcher: typeof fetch;
  protected credentials?: RequestCredentials;
  constructor(opts: ApiClientOptions) {
    this.base = opts.baseUrl;
    this.fetcher = resolveFetch(opts);
    this.credentials = opts.credentials ?? "include";
  }
  protected async post<T>(
    path: string,
    body: unknown,
    schema: z.ZodType,
    init?: MutationRequestOptions,
  ): Promise<T> {
    const res = await this.fetcher(buildUrl(this.base, path), {
      method: "POST",
      headers: mutationHeaders(init),
      credentials: this.credentials,
      body: JSON.stringify(body),
    });
    return handle<T>(res, schema);
  }
  protected async get<T>(
    path: string,
    schema: z.ZodType,
    init?: { cache?: RequestCache },
  ): Promise<T> {
    const res = await this.fetcher(buildUrl(this.base, path), {
      method: "GET",
      credentials: this.credentials,
      // Booking data is time-sensitive — never let Next.js's RSC fetch cache
      // (or any intermediary) serve a stale snapshot. Callers can override
      // by passing `init.cache` explicitly.
      cache: init?.cache ?? "no-store",
    });
    return handle<T>(res, schema);
  }
  protected async patch<T>(
    path: string,
    body: unknown,
    schema: z.ZodType,
    init?: MutationRequestOptions,
  ): Promise<T> {
    const res = await this.fetcher(buildUrl(this.base, path), {
      method: "PATCH",
      headers: mutationHeaders(init),
      credentials: this.credentials,
      body: JSON.stringify(body),
    });
    return handle<T>(res, schema);
  }
  protected async put<T>(
    path: string,
    body: unknown,
    schema: z.ZodType,
  ): Promise<T> {
    const res = await this.fetcher(buildUrl(this.base, path), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: this.credentials,
      body: JSON.stringify(body),
    });
    return handle<T>(res, schema);
  }
  protected async del<T>(path: string, schema: z.ZodType): Promise<T> {
    const res = await this.fetcher(buildUrl(this.base, path), {
      method: "DELETE",
      credentials: this.credentials,
    });
    return handle<T>(res, schema);
  }

  // ── Strict commerce verbs (no legacy success fallback) ──────────────────

  protected async getStrict<T>(
    path: string,
    schema: z.ZodType,
    init?: { cache?: RequestCache },
  ): Promise<T> {
    const res = await this.fetcher(buildUrl(this.base, path), {
      method: "GET",
      credentials: this.credentials,
      cache: init?.cache ?? "no-store",
    });
    return handleStrict<T>(res, schema);
  }

  protected async postStrict<T>(
    path: string,
    body: unknown,
    schema: z.ZodType,
    init?: MutationRequestOptions,
  ): Promise<T> {
    const res = await this.fetcher(buildUrl(this.base, path), {
      method: "POST",
      headers: mutationHeaders(init),
      credentials: this.credentials,
      body: JSON.stringify(body),
    });
    return handleStrict<T>(res, schema);
  }

  protected async putStrict<T>(
    path: string,
    body: unknown,
    schema: z.ZodType,
    init?: MutationRequestOptions,
  ): Promise<T> {
    const res = await this.fetcher(buildUrl(this.base, path), {
      method: "PUT",
      headers: mutationHeaders(init),
      credentials: this.credentials,
      body: JSON.stringify(body),
    });
    return handleStrict<T>(res, schema);
  }

  protected async patchStrict<T>(
    path: string,
    body: unknown,
    schema: z.ZodType,
    init?: MutationRequestOptions,
  ): Promise<T> {
    const res = await this.fetcher(buildUrl(this.base, path), {
      method: "PATCH",
      headers: mutationHeaders(init),
      credentials: this.credentials,
      body: JSON.stringify(body),
    });
    return handleStrict<T>(res, schema);
  }

  /**
   * Typed raw text download (CSV exports). The RESPONSE content type must be
   * on the allowlist; this helper deliberately adds no request-header
   * authority — callers still cannot set arbitrary headers.
   */
  protected async getRawText(
    path: string,
    init?: { accept?: readonly string[] },
  ): Promise<RawTextResponse> {
    const res = await this.fetcher(buildUrl(this.base, path), {
      method: "GET",
      credentials: this.credentials,
      cache: "no-store",
    });
    if (!res.ok) {
      const json = await res
        .json()
        .catch(() => null as unknown);
      const errParsed = baseErr.safeParse(json);
      if (errParsed.success) {
        throw Object.assign(new Error(errParsed.data.error.message), {
          code: errParsed.data.error.code,
          status: res.status,
        });
      }
      throw Object.assign(new Error(`Download failed (${res.status})`), {
        status: res.status,
      });
    }
    const contentType = res.headers?.get?.("content-type") ?? "";
    const accepted = init?.accept ?? RAW_TEXT_CONTENT_TYPES;
    if (!accepted.some((type) => contentType.startsWith(type))) {
      throw Object.assign(
        new Error(`Unexpected download content type: ${contentType || "unknown"}`),
        { status: res.status, code: "UNEXPECTED_CONTENT_TYPE" },
      );
    }
    const disposition = res.headers?.get?.("content-disposition") ?? "";
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? null;
    return { content: await res.text(), contentType, filename };
  }
}
